/**
 * ManufacturingLayout - Layout Component for Manufacturing Module
 *
 * Provides a consistent layout with sidebar navigation for all manufacturing pages.
 *
 * @author SAUBHAGYA Development Team
 * @since Epic 6
 */

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Package,
  Factory,
  CreditCard,
  ChevronLeft,
  Menu,
  User,
  LogOut,
  Loader2,
  Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { profileService } from '@/services/admin/profile.service';
import { toast } from 'sonner';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/manufacturing/dashboard',
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: Package,
    path: '/manufacturing/transactions',
  },
  {
    id: 'production',
    label: 'Production Batches',
    icon: Factory,
    path: '/manufacturing/production',
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: CreditCard,
    path: '/manufacturing/payments',
  },
  {
    id: 'raw-material-deliveries',
    label: 'Raw Material Deliveries',
    icon: Truck,
    path: '/manufacturing/raw-material-deliveries',
  },
];

export default function ManufacturingLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);

    // Call ProfileService logout (clears server session + local storage)
    await profileService.logout();

    // Also call AuthContext logout to clear local state
    await logout();

    // Show success message
    toast.success('Logged out successfully');

    setIsLoggingOut(false);
    setShowLogoutDialog(false);

    // Navigate to login page
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-16'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Factory className="h-6 w-6 text-primary" />
              <span className="font-semibold text-lg">Manufacturing</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto"
          >
            {sidebarOpen ? (
              <ChevronLeft className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex items-center w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                <Icon className={cn('h-5 w-5', sidebarOpen && 'mr-3')} />
                {sidebarOpen && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer - Profile & Logout Section */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 group">
            {/* Profile Avatar & Info */}
            <button
              onClick={() => navigate('/manufacturing/profile')}
              className="flex items-center gap-3 flex-1 text-left"
              title={user?.name || 'Profile'}
            >
              {/* Profile Avatar */}
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 text-white font-semibold">
                {user?.name ? user.name.charAt(0).toUpperCase() : <User className="h-5 w-5" />}
              </div>

              {/* Profile Info */}
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.phone || 'Profile'}
                  </p>
                </div>
              )}
            </button>

            {/* Logout Button - Right side */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogoutClick}
              className="h-8 w-8 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          'flex-1 transition-all duration-300 overflow-hidden',
          sidebarOpen ? 'ml-64' : 'ml-16'
        )}
      >
        <div className="h-screen overflow-y-auto">
          <div className="p-6 max-w-full">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to logout? You will need to login again to access your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleLogoutCancel}
              disabled={isLoggingOut}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogoutConfirm}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
